import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { useTheme, useMediaQuery, Box,
  CircularProgress, Typography, Tooltip, IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const CrewComplements = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "CrewComplements", "create", false);
  const canEdit   = hasPermission(user?.permissions, "CrewComplements", "update", false);
  const canDelete = hasPermission(user?.permissions, "CrewComplements", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [crewComplements, setCrewComplements] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const handleCrewComplements = async () => {
      try {
        setLoading(true);
        const response = await api.get('/crewcomplements');
        const result = response.data;
        isMounted && setCrewComplements(result);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleCrewComplements();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...isMobile ? { width: 60 } : { flex: 1 },
    },
    {
      field: "acTypeCode",
      headerName: "ACType",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
    {
      field: 'acRegs',
      headerName: 'ACRegs',
      ...isMobile ? { width: 100 } : { flex: 1.5 },
      renderCell: (params) => (
        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
          {params.row?.acRegs?.map((acReg, index) => (
            <FlexBetween key={index} gap={1}>
              <Typography variant="body2">{acReg}</Typography>
            </FlexBetween>
          ))}
        </Box>
      ),
    },
    {
      field: 'sectors',
      headerName: 'Sectors',
      ...isMobile ? { width: 100 } : { flex: 1.5 },
      renderCell: (params) => (
        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
          {params.row?.sectors?.map((sector, index) => (
            <FlexBetween key={index} gap={1}>
              <Typography>{sector}</Typography>
            </FlexBetween>
          ))}
        </Box>
      ),
    },
    {
      field: 'crewComplement',
      headerName: 'CrewComplement',
      ...isMobile ? { width: 100 } : { flex: 1.5 },
      renderCell: (params) => (
        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
          {params.row?.crewComplement?.map((rank, index) => (
            <Box key={index} display="flex" alignItems="center" justifyContent="flex-start" gap={5}>
              <Typography variant="body2" width={3}>{rank.rankCode}</Typography>
              <Typography variant="body2">{rank.quantity}</Typography>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
  ], [isMobile]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,
      sortable: false,
      width: 90,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ width: '100%', height: '100%' }}>
          <Tooltip title="Edit">
            <IconButton
              aria-label="Edit"
              sx={{
                color: primaryDark,
                transition: 'color 0.3s ease, background-color 0.3s ease',
                '&:hover': { color: greenDeep },
              }}
              onClick={() => { if (!canEdit) return; navigate(`/crewcomplements/${params.row._id}`); }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete"
              sx={{
                color: primaryDark,
                transition: 'color 0.3s ease, background-color 0.3s ease',
                '&:hover': { color: greenDeep },
              }}
              onClick={() => { if (!canDelete) return; handleDeleteClick(params.row._id); }}
            >
              <DeleteForever />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const gridStyles = {
    "& .MuiDataGrid-row.isActive-false": {
      backgroundColor: `${theme.palette.primary.light} !important`,
    },
  };

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, crewComplements);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/crewcomplements/delete/${idToDelete}`);
        setCrewComplements(crewComplements.filter((item) => item._id !== idToDelete));
        toast.success('Crew complement deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, crewComplements);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/crewcomplements/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setCrewComplements(crewComplements.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (error) {
      toast.error(error?.message || "Failed to delete crew complements!");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, crewComplements]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Crew Complements"
          subtitle={`${loading ? 'Loading...' : 'List of Crew Complements'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, crewComplements)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/crewcomplements/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={crewComplements}
                primaryField="acTypeCode"
                fields={[
                  { label: "ACRegs", key: "acRegs" },
                  { label: "Sectors", key: "sectors" },
                  { label: "Complement", key: "crewComplement" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/crewcomplements/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={crewComplements || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  sx: gridStyles,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default CrewComplements;
